import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { PollDto } from "@/api/types";
import { CountdownBadge } from "@/components/Badge/CountdownBadge";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { Pdf } from "@/components/Pdf/Pdf";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { twResolve } from "@/helpers/tw-resolve";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { CommunityContentRail } from "@/product-patterns/community/CommunityContentRail";
import { usePollQueries } from "@/queries/polls/queryOptions";
import { useTranslation } from "@/translations";

import { ChevronSelectorVerticalIcon } from "../Icons/ChevronSelectorVerticalIcon";
import { Globe02Icon } from "../Icons/Globe02Icon";
import { Video } from "../Video/Video";
import { CommunityPollResultSection } from "./CommunityPollResultSection";
import { CommunityPollVoteForm } from "./CommunityPollVoteForm";
import { CommunityPostGallery } from "./CommunityPostMedia/CommunityPostGallery";

type TruncationState = "detecting" | "truncated" | "expanded" | "none";

interface CommunityPollContentProps {
  poll: PollDto;
  shouldLoad?: boolean;
  onViewResults: () => void;
  isTranslated: boolean;
  onToggleTranslation: () => void;
}

export function CommunityPollContent({
  poll,
  shouldLoad,
  onViewResults,
  isTranslated,
  onToggleTranslation,
}: CommunityPollContentProps): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const pollQueries = usePollQueries();

  const hasAutoTranslation = !!poll.autoTranslation;

  const { data: fetchedTranslation, isLoading: isLoadingTranslation } = useQuery({
    ...pollQueries.translation({
      pollId: poll.id,
      languageId: sessionUser.autoTranslationLanguage?.id || sessionUser.language.id,
    }),
    enabled: isTranslated && !hasAutoTranslation,
  });

  const activeTranslation = isTranslated ? (poll.autoTranslation ?? fetchedTranslation) : undefined;

  const title = activeTranslation?.title ?? poll.title;
  const description = activeTranslation?.description ?? poll.description;
  const question = activeTranslation?.question ?? poll.question;
  const answers = activeTranslation?.answers ?? poll.answers;

  const isTranslatable = sessionUser.autoTranslationLanguage
    ? poll.languageIsoCode !== sessionUser.autoTranslationLanguage.id
    : poll.languageIsoCode !== sessionUser.language.id;
  const canBeTranslated = !hasAutoTranslation && poll.createdBy.id !== sessionUser.id && isTranslatable;
  const showTranslateToggle = (hasAutoTranslation && !poll.deletedAt) || canBeTranslated;

  const endDate = poll.endsAt ? new Date(poll.endsAt) : undefined;

  const [truncationState, setTruncationState] = useState<TruncationState>(description ? "detecting" : "none");
  const contentRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (truncationState !== "detecting") {
      return;
    }

    const el = contentRef.current;
    if (!el) {
      return;
    }

    setTruncationState(el.scrollHeight > el.clientHeight ? "truncated" : "none");
  }, [truncationState]);

  const handleReadMore = useCallback(() => {
    setTruncationState("expanded");
  }, []);

  return (
    <>
      <Stack gap="sm">
        <p
          className={twJoin(
            "text-body-emphasis whitespace-pre-line",
            poll.deletedAt && "text-text-secondary line-through",
          )}
          data-testid="post-title"
        >
          {title}
        </p>
        {description && (
          <span
            ref={contentRef}
            data-testid="post-content"
            className={twResolve(
              truncationState === "detecting" && "line-clamp-6",
              truncationState === "truncated" && "line-clamp-4",
              poll.deletedAt ? "text-text-secondary line-through" : "text-text-primary",
            )}
          >
            <MultiLineContent shouldHighlightLinks>{description}</MultiLineContent>
          </span>
        )}
        {isTranslated && poll.autoTranslation?.isAutoTranslated && <AutoTranslatedLabel />}
        {(truncationState === "truncated" || showTranslateToggle) && (
          <nav className="flex flex-row gap-space-lg py-space-sm">
            {truncationState === "truncated" && (
              <Button variant="ghostPrimary" onClick={handleReadMore} icon={<ChevronSelectorVerticalIcon />}>
                {t("component.community-post.content.read-more")}
              </Button>
            )}
            {showTranslateToggle && (
              <Button
                variant="ghostPrimary"
                data-testid="translate-btn"
                isLoading={isLoadingTranslation}
                onClick={onToggleTranslation}
                icon={<Globe02Icon />}
              >
                {isTranslated
                  ? t("component.community-post.content.translate.original")
                  : t("component.community-post.content.translate")}
              </Button>
            )}
          </nav>
        )}
      </Stack>
      <CommunityContentRail>
        <Stack gap="md">
          {poll.images.length > 0 && <CommunityPostGallery images={poll.images} shouldLoad={shouldLoad} />}
          {poll.videos.length > 0 && <Video video={poll.videos[0]} shouldLoad={shouldLoad} />}
          {poll.documents && poll.documents.length > 0 && (
            <Inline gap="sm">
              {poll.documents.map((x) => (
                <Pdf
                  key={x.id}
                  previewImage={x.previewImage}
                  fileName={x.fileName}
                  fileSize={x.fileSize}
                  onClick={() => window.open(x.url, "_blank")}
                />
              ))}
            </Inline>
          )}
          {/* Poll */}
          <Card as="section">
            {endDate && !poll.scheduledAt && !poll.deletedAt && <CountdownBadge endDate={endDate} />}
            <span
              data-testid="poll-question"
              className={twResolve("text-heading-3", poll.deletedAt && "text-text-secondary line-through")}
            >
              {question}
            </span>
            {poll.canRespond === "yes" ? (
              <CommunityPollVoteForm poll={poll} answers={answers} />
            ) : (
              <CommunityPollResultSection poll={poll} results={activeTranslation?.results ?? poll.results ?? []} />
            )}
            {poll.canSeeResults && (
              <footer className="mx-auto w-fit">
                <Button variant="ghostPrimary" onClick={onViewResults}>
                  {t("component.community-poll.button.view-results")}
                </Button>
              </footer>
            )}
          </Card>
        </Stack>
      </CommunityContentRail>
    </>
  );
}
